import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { evalService, processService, llmConfigService } from '../../services/api';
import EvalRunList from '../eval/EvalRunList';
import EvalRunDetail from '../eval/EvalRunDetail';

const EvalTestsTab = () => {
  const { projectId } = useParams();
  const [tests, setTests] = useState([]);
  const [stats, setStats] = useState(null);
  const [processes, setProcesses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'chat',
    processId: '',
    input: { message: '' },
    expectedCriteria: [{ criterion: '', weight: 1 }],
    evaluationMode: 'criteria_only',
    judgeModel: ''
  });
  const [editingId, setEditingId] = useState(null);
  
  // Run history
  const [selectedTestId, setSelectedTestId] = useState(null);
  const [runHistory, setRunHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeRunId, setActiveRunId] = useState(null);
  
  // Comparison state
  const [selectedRunIds, setSelectedRunIds] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  
  // Available models (loaded from API)
  const [availableModels, setAvailableModels] = useState([]);
  
  // Execution state
  const [executingTestId, setExecutingTestId] = useState(null);
  const [executingAll, setExecutingAll] = useState(false);
  
  // Pre-run config modal
  const [showRunConfig, setShowRunConfig] = useState(false);
  const [runConfigTarget, setRunConfigTarget] = useState(null); // testId or 'all'
  const [sliderPresets, setSliderPresets] = useState([]);
  const [runConfig, setRunConfig] = useState({
    sliderValue: 2,
    modelName: '',
    thinkingLevel: 'low',
    showAdvanced: false
  });

  const loadTests = useCallback(async () => {
    if (!projectId) return;
    
    setIsLoading(true);
    setError(null);
    try {
      const [testsResponse, statsResponse] = await Promise.all([
        evalService.getTestsByProject(projectId),
        evalService.getProjectStats(projectId)
      ]);
      setTests(testsResponse.data);
      setStats(statsResponse.data);
    } catch (err) {
      console.error('Error loading eval tests:', err);
      setError('Failed to load eval tests');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  const loadProcesses = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await processService.getProcessesByProject(projectId);
      setProcesses(response.data);
    } catch (err) {
      console.error('Error loading processes:', err);
    }
  }, [projectId]);

  const loadModels = useCallback(async () => {
    try {
      const response = await llmConfigService.getAvailableModels();
      setAvailableModels(response.data || []);
    } catch (err) {
      console.error('Error loading models:', err);
    }
  }, []);

  const loadSliderPresets = useCallback(async () => {
    try {
      const response = await llmConfigService.getSliderPresets();
      const presets = response.data || [];
      setSliderPresets(presets);
    } catch (err) {
      console.error('Error loading slider presets:', err);
    }
  }, []);

  useEffect(() => {
    loadTests();
    loadProcesses();
    loadModels();
    loadSliderPresets();
  }, [loadTests, loadProcesses, loadModels, loadSliderPresets]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      type,
      processId: type === 'analysis' ? prev.processId : '', // Keep processId for analysis, reset for chat
      input: type === 'chat' ? { message: '' } : { scope: 'full-process', analysisMode: 'standard' }
    }));
  };

  const handleCriterionChange = (index, field, value) => {
    setFormData(prev => {
      const newCriteria = [...prev.expectedCriteria];
      newCriteria[index] = { ...newCriteria[index], [field]: value };
      return { ...prev, expectedCriteria: newCriteria };
    });
  };

  const addCriterion = () => {
    setFormData(prev => ({
      ...prev,
      expectedCriteria: [...prev.expectedCriteria, { criterion: '', weight: 1 }]
    }));
  };

  const removeCriterion = (index) => {
    setFormData(prev => ({
      ...prev,
      expectedCriteria: prev.expectedCriteria.filter((_, i) => i !== index)
    }));
  };

  const getDefaultFormData = () => ({
    name: '',
    description: '',
    type: 'chat',
    processId: '',
    input: { message: '' },
    expectedCriteria: [{ criterion: '', weight: 1 }],
    evaluationMode: 'criteria_only',
    judgeModel: availableModels[0]?.modelName || ''
  });

  const resetForm = () => {
    setFormData(getDefaultFormData());
    setEditingId(null);
    setShowForm(false);
  };

  const openNewTestForm = () => {
    setFormData(getDefaultFormData());
    setEditingId(null);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      projectId,
      name: formData.name,
      description: formData.description || null,
      type: formData.type,
      processId: formData.processId || null,
      input: formData.input,
      expectedCriteria: formData.expectedCriteria.filter(c => c.criterion.trim()),
      evaluationMode: formData.evaluationMode || 'criteria_only'
    };

    try {
      if (editingId) {
        await evalService.updateTest(editingId, payload);
      } else {
        await evalService.createTest(payload);
      }
      resetForm();
      loadTests();
    } catch (err) {
      console.error('Error saving test:', err);
      alert('Failed to save test: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEdit = (test) => {
    setFormData({
      name: test.name,
      description: test.description || '',
      type: test.type,
      processId: test.processId || '',
      input: test.input,
      expectedCriteria: test.expectedCriteria,
      evaluationMode: test.evaluationMode || 'criteria_only',
      judgeModel: test.judgeModel || availableModels[0]?.modelName || ''
    });
    setEditingId(test.id);
    setShowForm(true);
  };

  const handleDelete = async (testId) => {
    if (!window.confirm('Are you sure you want to delete this test?')) return;
    
    try {
      await evalService.deleteTest(testId);
      loadTests();
    } catch (err) {
      console.error('Error deleting test:', err);
      alert('Failed to delete test');
    }
  };

  const openRunConfig = (target) => {
    const defaultPreset = sliderPresets.find(p => p.sliderPosition === 3) || sliderPresets[0];
    setRunConfig({
      sliderValue: defaultPreset?.sliderPosition || 2,
      modelName: defaultPreset?.modelName || availableModels[0]?.modelName || '',
      thinkingLevel: defaultPreset?.thinkingLevel?.toLowerCase() || 'low',
      showAdvanced: false
    });
    setRunConfigTarget(target);
    setShowRunConfig(true);
  };

  const handleSliderChange = (value) => {
    const preset = sliderPresets.find(p => p.sliderPosition === value);
    if (preset) {
      setRunConfig(prev => ({
        ...prev,
        sliderValue: value,
        modelName: preset.modelName,
        thinkingLevel: preset.thinkingLevel?.toLowerCase() || 'low'
      }));
    } else {
      setRunConfig(prev => ({ ...prev, sliderValue: value }));
    }
  };

  const executeWithConfig = async () => {
    setShowRunConfig(false);
    const config = { modelName: runConfig.modelName, thinkingLevel: runConfig.thinkingLevel };

    if (runConfigTarget === 'all') {
      setExecutingAll(true);
      try {
        await evalService.runAllTests(projectId, true, config);
        loadTests();
      } catch (err) {
        console.error('Error running all tests:', err);
        alert('Failed to run tests: ' + (err.response?.data?.error || err.message));
      } finally {
        setExecutingAll(false);
      }
    } else {
      setExecutingTestId(runConfigTarget);
      try {
        await evalService.runTest(runConfigTarget, config);
        loadTests();
        if (selectedTestId === runConfigTarget) {
          const response = await evalService.getTestRuns(runConfigTarget);
          setRunHistory(response.data);
          if (response.data.length > 0) {
            setActiveRunId(response.data[0].id);
          }
        }
      } catch (err) {
        console.error('Error running test:', err);
        alert('Failed to run test: ' + (err.response?.data?.error || err.message));
      } finally {
        setExecutingTestId(null);
      }
    }
  };

  const loadRunHistory = async (testId) => {
    if (selectedTestId === testId) {
      setSelectedTestId(null);
      setSelectedRunIds([]);
      setShowComparison(false);
      setActiveRunId(null);
      return;
    }
    
    setSelectedTestId(testId);
    setSelectedRunIds([]);
    setShowComparison(false);
    setActiveRunId(null);
    setLoadingHistory(true);
    try {
      const response = await evalService.getTestRuns(testId);
      setRunHistory(response.data);
      if (response.data.length > 0) {
        setActiveRunId(response.data[0].id);
      }
    } catch (err) {
      console.error('Error loading run history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const toggleRunSelection = (runId) => {
    setSelectedRunIds(prev => 
      prev.includes(runId) ? prev.filter(id => id !== runId) : [...prev, runId]
    );
  };

  const getScoreBadge = (score, passed) => {
    const bgColor = passed ? 'bg-green-100' : score >= 50 ? 'bg-yellow-100' : 'bg-red-100';
    const textColor = passed ? 'text-green-800' : score >= 50 ? 'text-yellow-800' : 'text-red-800';
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {score}%
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center rounded-lg" style={{background: 'var(--tqa-black)'}}>
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tqa-accent mb-4"></div>
          <p style={{color: 'rgba(255,255,255,0.5)'}}>Loading Eval Tests...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center rounded-lg" style={{background: 'var(--tqa-black)'}}>
        <div className="text-center">
          <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-semibold mb-2" style={{color: 'rgba(255,255,255,0.85)'}}>Error Loading Data</h3>
          <p className="mb-4" style={{color: 'rgba(255,255,255,0.65)'}}>{error}</p>
          <button onClick={loadTests} className="btn-primary">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col rounded-lg overflow-hidden" style={{background: 'var(--tqa-black)'}}>
      {/* Header with Stats */}
      <div className="px-6 py-4" style={{borderBottom: '1px solid var(--tqa-border-dark)', borderTop: '1px solid var(--tqa-green)', background: 'var(--tqa-surface)'}}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Eval Tests
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => openRunConfig('all')}
              disabled={executingAll || tests.length === 0}
              className="px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {executingAll ? 'Running...' : 'Run All Tests'}
            </button>
            <button
              onClick={openNewTestForm}
              className="btn-secondary px-3 py-1.5 text-sm font-medium"
            >
              + New Test
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="rounded-lg px-3 py-2" style={{background: 'rgba(255,255,255,0.08)'}}>
              <div className="text-xs font-medium" style={{color: 'rgba(255,255,255,0.65)'}}>Total Tests</div>
              <div className="text-2xl font-bold" style={{color: 'rgba(255,255,255,0.9)'}}>{stats.totalTests}</div>
            </div>
            <div className="rounded-lg px-3 py-2" style={{background: 'rgba(255,255,255,0.08)'}}>
              <div className="text-xs font-medium" style={{color: 'rgba(255,255,255,0.65)'}}>Passed</div>
              <div className="text-2xl font-bold" style={{color: 'var(--tqa-green)'}}>{stats.latestResults?.passed || 0}</div>
            </div>
            <div className="rounded-lg px-3 py-2" style={{background: 'rgba(255,255,255,0.08)'}}>
              <div className="text-xs font-medium" style={{color: 'rgba(255,255,255,0.65)'}}>Failed</div>
              <div className="text-2xl font-bold" style={{color: 'var(--tqa-red)'}}>{stats.latestResults?.failed || 0}</div>
            </div>
            <div className="rounded-lg px-3 py-2" style={{background: 'rgba(255,255,255,0.08)'}}>
              <div className="text-xs font-medium" style={{color: 'rgba(255,255,255,0.65)'}}>Not Run</div>
              <div className="text-2xl font-bold" style={{color: 'rgba(255,200,100,0.9)'}}>{stats.latestResults?.notRun || 0}</div>
            </div>
            <div className="rounded-lg px-3 py-2" style={{background: 'rgba(255,255,255,0.08)'}}>
              <div className="text-xs font-medium" style={{color: 'rgba(255,255,255,0.65)'}}>Avg Score</div>
              <div className="text-2xl font-bold" style={{color: 'rgba(255,255,255,0.9)'}}>{stats.averageScore || 0}%</div>
            </div>
          </div>
        )}
      </div>

      {/* Test List */}
      <div className="flex-1 overflow-y-auto p-6">
        {showForm && (
          <div className="mb-6 p-4 rounded-lg" style={{border: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface)'}}>
            <h3 className="text-lg font-semibold mb-4" style={{color: 'rgba(255,255,255,0.85)'}}>
              {editingId ? 'Edit Test' : 'New Eval Test'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{color: 'rgba(255,255,255,0.65)'}}>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-tqa-accent"
                    style={{border: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)', color: 'rgba(255,255,255,0.85)'}}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{color: 'rgba(255,255,255,0.65)'}}>Type *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleTypeChange(e.target.value)}
                    className="w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-tqa-accent"
                    style={{border: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)', color: 'rgba(255,255,255,0.85)'}}>
                    <option value="chat">Chat Question</option>
                    <option value="analysis">Code Analysis</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{color: 'rgba(255,255,255,0.65)'}}>
                  Process {formData.type === 'analysis' ? '*' : '(optional)'}
                </label>
                <select
                  value={formData.processId}
                  onChange={(e) => handleInputChange('processId', e.target.value)}
                  className="w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-tqa-accent"
                  style={{border: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)', color: 'rgba(255,255,255,0.85)'}}
                  required={formData.type === 'analysis'}
                >
                  {formData.type === 'chat' && <option value="">All processes</option>}
                  {formData.type === 'analysis' && !formData.processId && <option value="">Select a process...</option>}
                  {processes.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                {formData.type === 'analysis' && (
                  <p className="text-xs mt-1" style={{color: 'rgba(255,255,255,0.4)'}}>Analysis tests require a specific process</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{color: 'rgba(255,255,255,0.65)'}}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-tqa-accent"
                  style={{border: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)', color: 'rgba(255,255,255,0.85)'}}
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 flex items-center gap-1" style={{color: 'rgba(255,255,255,0.65)'}}>
                    Evaluation Mode
                    <span 
                      className="inline-flex items-center justify-center w-4 h-4 rounded-full cursor-help text-[10px]"
                      style={{background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.65)'}}
                      title="Criteria Only: evaluates against your defined criteria. Criteria + Quality: also evaluates standard quality dimensions (Accuracy, Completeness, Relevance, Actionability, Clarity). Quality Only: evaluates only standard quality dimensions without specific criteria."
                    >
                      ?
                    </span>
                  </label>
                  <select
                    value={formData.evaluationMode}
                    onChange={(e) => handleInputChange('evaluationMode', e.target.value)}
                    className="w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-tqa-accent"
                    style={{border: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)', color: 'rgba(255,255,255,0.85)'}}>
                    <option value="criteria_only">Criteria Only</option>
                    <option value="criteria_and_free">Criteria + Quality Dimensions</option>
                    <option value="free_only">Quality Dimensions Only</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{color: 'rgba(255,255,255,0.65)'}}>Judge Model</label>
                  <select
                    value={formData.judgeModel}
                    onChange={(e) => handleInputChange('judgeModel', e.target.value)}
                    className="w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-tqa-accent"
                    style={{border: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)', color: 'rgba(255,255,255,0.85)'}}>
                    {availableModels.map(model => (
                      <option key={model.modelName} value={model.modelName}>
                        {model.displayName || model.modelName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Input based on type */}
              {formData.type === 'chat' ? (
                <div>
                  <label className="block text-sm font-medium mb-1" style={{color: 'rgba(255,255,255,0.65)'}}>Chat Message *</label>
                  <textarea
                    value={formData.input.message || ''}
                    onChange={(e) => handleInputChange('input', { message: e.target.value })}
                    className="w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-tqa-accent"
                    style={{border: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)', color: 'rgba(255,255,255,0.85)'}}
                    rows={3}
                    placeholder="Enter the question to ask the chat..."
                    required
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-1" style={{color: 'rgba(255,255,255,0.65)'}}>Analysis Mode</label>
                  <select
                    value={formData.input.analysisMode || 'standard'}
                    onChange={(e) => handleInputChange('input', { scope: 'full-process', analysisMode: e.target.value })}
                    className="w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-tqa-accent"
                    style={{border: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)', color: 'rgba(255,255,255,0.85)'}}>
                    <option value="standard">Standard</option>
                    <option value="comprehensive">Comprehensive</option>
                  </select>
                  <p className="text-xs mt-1" style={{color: 'rgba(255,255,255,0.4)'}}>Will analyze the entire selected process</p>
                </div>
              )}

              {/* Expected Criteria */}
              {formData.evaluationMode !== 'free_only' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium mb-2" style={{color: 'rgba(255,255,255,0.65)'}}>Expected Criteria *</label>
                  <button type="button" onClick={addCriterion} className="text-sm" style={{color: 'var(--tqa-green)'}}>
                    + Add Criterion
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.expectedCriteria.map((c, index) => (
                    <div key={index} className="flex gap-2 items-end">
                      <div className="flex-1">
                        {index === 0 && (
                          <div className="text-xs font-medium mb-1" style={{color: 'rgba(255,255,255,0.5)'}}>Criterion</div>
                        )}
                        <input
                          type="text"
                          value={c.criterion}
                          onChange={(e) => handleCriterionChange(index, 'criterion', e.target.value)}
                          className="w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-tqa-accent"
                          style={{border: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)', color: 'rgba(255,255,255,0.85)'}}
                          placeholder="Describe what the output should contain or demonstrate..."
                        />
                      </div>
                      <div className="w-20">
                        {index === 0 && (
                          <div className="text-xs font-medium mb-1 flex items-center justify-center gap-1" style={{color: 'rgba(255,255,255,0.5)'}}>
                              Weight
                              <span 
                                className="inline-flex items-center justify-center w-4 h-4 rounded-full cursor-help text-[10px]"
                                style={{background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.65)'}}
                              title="Weight determines the importance of each criterion in the final score. Higher weight = more impact on the result. Default is 1."
                            >
                              ?
                            </span>
                          </div>
                        )}
                        <input
                          type="number"
                          value={c.weight}
                          onChange={(e) => handleCriterionChange(index, 'weight', parseFloat(e.target.value) || 1)}
                          className="w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-tqa-accent"
                          style={{border: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)', color: 'rgba(255,255,255,0.85)'}}
                          min="0.1"
                          step="0.1"
                        />
                      </div>
                      {formData.expectedCriteria.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCriterion(index)}
                          className="px-2 py-2 text-red-500 hover:text-red-700"
                        >
                          X
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              )}

              {/* Quality Dimensions Info */}
              {formData.evaluationMode !== 'criteria_only' && (
                <div className="rounded-md p-3" style={{background: 'rgba(130,255,207,0.06)', border: '1px solid rgba(130,255,207,0.25)'}}>
                  <div className="text-sm font-medium mb-1" style={{color: 'var(--tqa-green)'}}>Standard Quality Dimensions</div>
                  <p className="text-xs mb-2" style={{color: 'rgba(255,255,255,0.5)'}}>The judge will also evaluate these dimensions automatically:</p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {['Accuracy', 'Completeness', 'Relevance', 'Actionability', 'Structure & Clarity'].map(dim => (
                      <span key={dim} className="px-2 py-1 rounded text-xs text-center" style={{background: 'rgba(130,255,207,0.1)', color: 'var(--tqa-green)'}}>{dim}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button type="button" onClick={resetForm} className="btn-secondary px-4 py-2">
                  Cancel
                </button>
                <button type="submit" className="btn-primary px-4 py-2">
                  {editingId ? 'Update Test' : 'Create Test'}
                </button>
              </div>
            </form>
          </div>
        )}

        {tests.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-16 h-16 mx-auto mb-4" style={{color: 'rgba(255,255,255,0.3)'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-semibold mb-2" style={{color: 'rgba(255,255,255,0.85)'}}>No Eval Tests Yet</h3>
            <p className="mb-4" style={{color: 'rgba(255,255,255,0.5)'}}>Create your first test to start evaluating AI outputs.</p>
            <button onClick={openNewTestForm} className="btn-primary">
              Create First Test
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {tests.map((test) => (
              <div key={test.id} className="rounded-lg p-4 transition-shadow" style={{border: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)'}}>
                {/* Header with title and actions */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold" style={{color: 'rgba(255,255,255,0.85)'}}>{test.name}</h3>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        test.type === 'chat' ? 'bg-blue-900/50 text-blue-300' : 'bg-orange-900/50 text-orange-300'
                      }`}>
                        {test.type === 'chat' ? 'Chat' : 'Analysis'}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        test.evaluationMode === 'free_only' ? 'bg-indigo-900/50 text-indigo-300' :
                        test.evaluationMode === 'criteria_and_free' ? 'bg-teal-900/50 text-teal-300' :
                        'bg-gray-700/50 text-gray-300'
                      }`}>
                        {test.evaluationMode === 'free_only' ? 'Quality Only' :
                         test.evaluationMode === 'criteria_and_free' ? 'Criteria + Quality' :
                         'Criteria Only'}
                      </span>
                      {!test.isActive && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium" style={{background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.45)'}}>
                          Inactive
                        </span>
                      )}
                    </div>
                    {test.description && (
                      <p className="text-sm mb-1" style={{color: 'rgba(255,255,255,0.65)'}}>{test.description}</p>
                    )}
                    {test.process && (
                      <p className="text-xs" style={{color: 'rgba(255,255,255,0.5)'}}>Process: {test.process.name}</p>
                    )}
                  </div>
                  {/* Score badge and Edit/Delete actions */}
                  <div className="flex items-center gap-3">
                    {test.latestRun && getScoreBadge(test.latestRun.score, test.latestRun.passed)}
                    <div className="flex items-center gap-1 text-xs">
                      <button
                        onClick={() => handleEdit(test)}
                        className="p-1 transition-colors"
                        style={{color: 'rgba(255,255,255,0.4)'}}
                        onMouseEnter={e => e.currentTarget.style.color='var(--tqa-green)'}
                        onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.4)'}
                        title="Edit test"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(test.id)}
                        className="p-1 transition-colors"
                        style={{color: 'rgba(255,255,255,0.4)'}}
                        onMouseEnter={e => e.currentTarget.style.color='var(--tqa-red)'}
                        onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.4)'}
                        title="Delete test"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Input preview */}
                <div className="rounded-md p-3 mb-3" style={{background: 'var(--tqa-surface-3)'}}>
                  <div className="text-xs mb-1" style={{color: 'rgba(255,255,255,0.5)'}}>Input:</div>
                  <div className="text-sm line-clamp-2" style={{color: 'rgba(255,255,255,0.75)'}}>
                    {test.type === 'chat' 
                      ? test.input.message 
                      : `Scope: ${test.input.scope}, Mode: ${test.input.analysisMode}`
                    }
                  </div>
                </div>

                {/* Criteria preview */}
                <div className="mb-3">
                  <div className="text-xs mb-1" style={{color: 'rgba(255,255,255,0.5)'}}>Criteria ({test.expectedCriteria?.length || 0}):</div>
                  <div className="flex flex-wrap gap-1">
                    {test.expectedCriteria?.slice(0, 3).map((c, i) => (
                      <span key={i} className="px-2 py-0.5 rounded text-xs truncate max-w-xs" style={{background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)'}}>
                        {c.criterion}
                      </span>
                    ))}
                    {test.expectedCriteria?.length > 3 && (
                      <span className="px-2 py-0.5 rounded text-xs" style={{background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)'}}>
                        +{test.expectedCriteria.length - 3} more
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions - Run button and footer */}
                <div className="flex items-center justify-between">
                  {/* Run button - Primary action */}
                  <button
                    onClick={() => openRunConfig(test.id)}
                    disabled={executingTestId === test.id}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      executingTestId === test.id 
                        ? 'rounded-lg font-medium cursor-not-allowed' 
                        : 'bg-green-500 text-white hover:bg-green-600 rounded-lg font-medium shadow-sm hover:shadow'
                    }`}
                    style={executingTestId === test.id ? {background: 'var(--tqa-surface-3)', color: 'rgba(255,255,255,0.4)'} : {}}
                  >
                    {executingTestId === test.id ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Running...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                        Run Test
                      </>
                    )}
                  </button>

                  {/* Last run info and History toggle */}
                  <div className="flex items-center gap-3">
                    {test.latestRun && (
                      <span className="text-xs" style={{color: 'rgba(255,255,255,0.4)'}}>
                        Last run: {new Date(test.latestRun.createdAt).toLocaleString()}
                      </span>
                    )}
                    <button
                      onClick={() => loadRunHistory(test.id)}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors ${
                        selectedTestId === test.id 
                          ? '' 
                          : ''
                      }`}
                      style={selectedTestId === test.id 
                        ? {background: 'rgba(130,255,207,0.1)', color: 'var(--tqa-green)'} 
                        : {color: 'rgba(255,255,255,0.5)'}}
                      onMouseEnter={e => { if (selectedTestId !== test.id) e.currentTarget.style.color='rgba(255,255,255,0.75)'; }}
                      onMouseLeave={e => { if (selectedTestId !== test.id) e.currentTarget.style.color='rgba(255,255,255,0.5)'; }}
                    >
                      <svg 
                        className={`w-4 h-4 transition-transform ${selectedTestId === test.id ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                      <span>History</span>
                    </button>
                  </div>
                </div>

                {/* Run History - Sidebar + Detail Layout */}
                {selectedTestId === test.id && (
                  <div className="mt-3 pt-3" style={{borderTop: '1px solid var(--tqa-border-dark)'}}>
                    {loadingHistory ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6" style={{borderBottom: '2px solid var(--tqa-green)'}}></div>
                      </div>
                    ) : runHistory.length === 0 ? (
                      <p className="text-sm text-center py-4" style={{color: 'rgba(255,255,255,0.5)'}}>No run history yet. Run the test to see results.</p>
                    ) : (
                      <div className="flex rounded-lg overflow-hidden" style={{border: '1px solid var(--tqa-border-dark)', height: '480px'}}>
                        <EvalRunList
                          runs={runHistory}
                          activeRunId={activeRunId}
                          onSelectRun={(id) => setActiveRunId(id)}
                          selectedRunIds={selectedRunIds}
                          onToggleRunSelection={toggleRunSelection}
                          onCompare={() => setShowComparison(!showComparison)}
                          showComparison={showComparison}
                        />
                        <EvalRunDetail
                          runs={runHistory}
                          activeRunId={activeRunId}
                          selectedRunIds={selectedRunIds}
                          showComparison={showComparison}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Run Configuration Modal */}
      {showRunConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="rounded-xl shadow-2xl w-full max-w-md" style={{background: 'var(--tqa-surface)'}}>
            <div className="p-5" style={{borderBottom: '1px solid var(--tqa-border-dark)'}}>
              <h3 className="text-lg font-semibold" style={{color: 'rgba(255,255,255,0.85)'}}>
                {runConfigTarget === 'all' ? 'Run All Tests' : 'Run Test'}
              </h3>
              <p className="text-sm mt-1" style={{color: 'rgba(255,255,255,0.5)'}}>Select the model and thinking level for this execution</p>
            </div>

            <div className="p-5 space-y-4">
              {/* Slider */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: 'rgba(255,255,255,0.65)'}}>Response Mode</label>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="1"
                  value={runConfig.sliderValue}
                  onChange={(e) => handleSliderChange(parseInt(e.target.value, 10))}
                  className="w-full"
                />
                <div className="flex items-center justify-between text-xs font-medium mt-1" style={{color: 'rgba(255,255,255,0.5)'}}>
                  <span>Faster</span>
                  <span>Smarter</span>
                </div>
              </div>

              {/* Speed/Smart bars */}
<div className="rounded-lg p-2.5" style={{border: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)'}}>
                  <div className="space-y-1.5">
                    <div className="flex h-4 items-center gap-2">
                      <span className="w-10 text-xs font-medium" style={{color: 'rgba(255,255,255,0.6)'}}>Speed</span>
                      {[1, 2, 3].map((bar) => (
                        <div
                          key={`speed-${bar}`}
                          className={`h-1.5 flex-1 rounded-sm transition-all`}
                          style={{background: bar <= (4 - runConfig.sliderValue) ? 'var(--tqa-green)' : 'rgba(255,255,255,0.12)'}}
                        />
                      ))}
                    </div>
                    <div className="flex h-4 items-center gap-2">
                      <span className="w-10 text-xs font-medium" style={{color: 'rgba(255,255,255,0.6)'}}>Smart</span>
                      {[1, 2, 3].map((bar) => (
                        <div
                          key={`smart-${bar}`}
                          className={`h-1.5 flex-1 rounded-sm transition-all`}
                          style={{background: bar <= runConfig.sliderValue ? 'var(--tqa-green)' : 'rgba(255,255,255,0.12)'}}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Current selection summary */}
<div className="flex items-center justify-between text-sm rounded-lg px-3 py-2" style={{background: 'rgba(130,255,207,0.08)', border: '1px solid rgba(130,255,207,0.25)'}}>
                  <span className="font-medium" style={{color: 'var(--tqa-green)'}}>
                    {availableModels.find(m => m.modelName === runConfig.modelName)?.displayName || runConfig.modelName}
                  </span>
                  <span className="px-2 py-0.5 rounded text-xs font-medium" style={{background: 'rgba(130,255,207,0.15)', color: 'var(--tqa-green)'}}>
                  {runConfig.thinkingLevel}
                </span>
              </div>

              {/* Advanced toggle */}
                <div className="rounded-lg overflow-hidden" style={{border: '1px solid var(--tqa-border-dark)'}}>
                <button
                  type="button"
                  onClick={() => setRunConfig(prev => ({ ...prev, showAdvanced: !prev.showAdvanced }))}
                  className="w-full px-3 py-2 flex items-center justify-between transition-colors"
                  style={{background: 'var(--tqa-surface-2)'}}
                  onMouseEnter={e => e.currentTarget.style.background='var(--tqa-surface-3)'}
                  onMouseLeave={e => e.currentTarget.style.background='var(--tqa-surface-2)'}
                >
                  <span className="text-xs font-semibold" style={{color: 'rgba(255,255,255,0.75)'}}>Advanced selection</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${runConfig.showAdvanced ? 'rotate-180' : ''}`}
                    style={{color: 'rgba(255,255,255,0.5)'}}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {runConfig.showAdvanced && (
                  <div className="p-3 space-y-3" style={{borderTop: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-3)'}}>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{color: 'rgba(255,255,255,0.65)'}}>Model</label>
                      <select
                        value={runConfig.modelName}
                        onChange={(e) => setRunConfig(prev => ({ ...prev, modelName: e.target.value }))}
                        className="w-full px-2 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-tqa-accent"
                        style={{border: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)', color: 'rgba(255,255,255,0.85)'}}>
                        {availableModels.map(model => (
                          <option key={model.modelName} value={model.modelName}>
                            {model.displayName || model.modelName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{color: 'rgba(255,255,255,0.65)'}}>Thinking level</label>
                      <select
                        value={runConfig.thinkingLevel}
                        onChange={(e) => setRunConfig(prev => ({ ...prev, thinkingLevel: e.target.value }))}
                        className="w-full px-2 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-tqa-accent"
                        style={{border: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)', color: 'rgba(255,255,255,0.85)'}}>
                        {['minimal', 'low', 'medium', 'high'].map(level => (
                          <option
                            key={level}
                            value={level}
                            disabled={level === 'minimal' && runConfig.modelName !== 'gemini-3-flash-preview'}
                          >
                            {level}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 flex justify-end gap-2" style={{borderTop: '1px solid var(--tqa-border-dark)'}}>
              <button
                onClick={() => setShowRunConfig(false)}
                className="btn-secondary px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={executeWithConfig}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
                Run
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvalTestsTab;
